import { useMemo } from 'react';
import { useGetGeneratedFilesTodayQuery } from '../../../services/rgrlVinculationApi';
import { useGetGeneratedRoamFilesTodayQuery } from '../../../services/roamApi';
import { GRV } from '../../../constants/values';
import { isAutoseguro } from '../../../constants/organizationLegends';
import type { FilaContadorCliente, TipoContadorArchivos } from '../consts/types';

/** El contexto guarda el id del Cliente, pero se tolera que llegue el objeto. */
export const normalizarIdOrganizacion = (organizacion: unknown): number | null => {
    const id =
        organizacion !== null && typeof organizacion === 'object'
            ? (organizacion as { id?: unknown }).id
            : organizacion;
    if (id === null || id === undefined || id === '') return null;
    const numero = Number(id);
    return Number.isFinite(numero) ? numero : null;
};

// Otro usuario pudo generar archivos mientras tanto: al volver a la pantalla
// (o a la tab) se pide de nuevo en vez de mostrar el conteo cacheado.
const OPCIONES_QUERY = { refetchOnMountOrArgChange: true } as const;

/**
 * El desglose lista los mismos clientes que el selector de la tab: GRV no es
 * un cliente (el resto de la app lo saca con `filterOrganizations`) y RGRL y
 * Visitas tampoco ofrecen los autoseguros.
 */
const esClienteVisible = (nombre: string, tipo: TipoContadorArchivos) =>
    nombre !== GRV && (tipo === 'roam' || !isAutoseguro(nombre));

interface UseArchivosGeneradosHoyParams {
    tipo: TipoContadorArchivos;
    /** Cliente elegido en la tab; `null` = todos. */
    idOrganizacion: number | null;
}

/**
 * Contador de archivos SRT generados en el día para el indicador del header,
 * del flujo de la tab activa.
 *
 * Visitas y RGRL/RRL salen de Vinculación RGRL y ROAM de Back-APIRoAm: solo se
 * pide el servicio de la tab. El total se suma con las filas visibles, para que
 * coincida con el desglose. Si la query falla, `cantidad` queda en `null` (el
 * indicador muestra "—").
 */
const useArchivosGeneradosHoy = ({ tipo, idOrganizacion }: UseArchivosGeneradosHoyParams) => {
    const esRoam = tipo === 'roam';

    const visitasRgrl = useGetGeneratedFilesTodayQuery(undefined, {
        ...OPCIONES_QUERY,
        skip: esRoam
    });
    const roam = useGetGeneratedRoamFilesTodayQuery(undefined, {
        ...OPCIONES_QUERY,
        skip: !esRoam
    });
    const query = esRoam ? roam : visitasRgrl;

    // Con error se descarta lo cacheado: el número va como "—".
    const datosVisitasRgrl = visitasRgrl.isError ? undefined : visitasRgrl.data;
    const datosRoam = roam.isError ? undefined : roam.data;
    const hayDatos = Boolean(esRoam ? datosRoam : datosVisitasRgrl);

    const filas: FilaContadorCliente[] = useMemo(() => {
        const clientes: FilaContadorCliente[] = esRoam
            ? (datosRoam?.clientes ?? []).map((cliente) => ({
                  idOrganizacion: Number(cliente.idOrganizacion),
                  nombre: cliente.nombre,
                  cantidad: cliente.roam ?? 0
              }))
            : (datosVisitasRgrl?.clientes ?? []).map((cliente) => ({
                  idOrganizacion: Number(cliente.idOrganizacion),
                  nombre: cliente.nombre,
                  cantidad: (tipo === 'visitas' ? cliente.visitas : cliente.rgrl) ?? 0
              }));
        return clientes.filter((cliente) => esClienteVisible(cliente.nombre, tipo));
    }, [esRoam, tipo, datosVisitasRgrl, datosRoam]);

    const cantidad = useMemo(() => {
        if (!hayDatos) return null;
        if (idOrganizacion === null) {
            return filas.reduce((total, fila) => total + fila.cantidad, 0);
        }
        // Con Cliente elegido que hoy no generó nada: 0.
        return filas.find((fila) => fila.idOrganizacion === idOrganizacion)?.cantidad ?? 0;
    }, [hayDatos, filas, idOrganizacion]);

    return {
        cantidad,
        filas,
        idOrganizacionSeleccionada: idOrganizacion,
        isLoading: query.isLoading,
        isError: query.isError
    };
};

export default useArchivosGeneradosHoy;
